import { BrandLockup } from '../../components/BrandLockup.js'
import { USER_ROLE_LABELS, type Tenant, type User, type UserRole } from '@blueprint/domain'
import { useCallback, useEffect, useState } from 'react'
import { Button } from '../../components/controls.js'
import { Dialog } from '../../components/Dialog.js'
import { UserMenu } from '../../app/UserMenu.js'
import { RepositoryError } from '../../data/index.js'
import { sessionRepository, useSessionStore } from '../../state/session-store.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from './AdminView.module.css'

/**
 * What a TENANT_ADMIN manages: the people in their own tenant. Never boards — an admin
 * reaches a board only by owning it or being shared it, exactly like everyone else.
 */
export function UsersView({ onClose }: { onClose: () => void }) {
  const repository = sessionRepository()
  const me = useSessionStore((state) => state.user)
  const sessionExpired = useSessionStore((state) => state.sessionExpired)
  const requestConfirm = useUiStore((state) => state.requestConfirm)
  const [users, setUsers] = useState<User[] | null>(null)
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<User | null>(null)
  const [creating, setCreating] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  // People are invited through Teamcentric Accounts and set their own password there; with
  // it on, a person with a teamcentric login has the name they set there.
  const [accounts, setAccounts] = useState(false)

  useEffect(() => {
    repository.authConfig().then((config) => setAccounts(config.accounts === true), () => {})
  }, [repository])

  const refresh = useCallback(async () => {
    try {
      setUsers(await repository.listUsers())
      setTenant(await repository.getTenant())
      setError(null)
    } catch (caught) {
      if (caught instanceof RepositoryError && caught.kind === 'unauthorized') return sessionExpired()
      setError(caught instanceof Error ? caught.message : 'The users could not be loaded.')
    }
  }, [repository, sessionExpired])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const invitationOutcome = (sent: boolean) =>
    sent
      ? 'Invitation sent: Teamcentric Accounts emails them a link to set up their teamcentric login.'
      : 'The user was added, but the invitation could not be sent. Use Re-invite to try again.'

  const reinvite = (user: User) => {
    setNotice(null)
    void repository
      .reinviteUser(user.id)
      .then((sent) => {
        setNotice(sent ? invitationOutcome(true) : 'The invitation could not be sent. Try again later.')
        return refresh()
      })
      .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : 'The invitation could not be sent.'))
  }

  const setDisabled = (user: User, disabled: boolean) => {
    const apply = () => {
      void repository
        .updateUser(user.id, { disabled })
        .then(refresh)
        .catch((caught: unknown) =>
          setError(caught instanceof Error ? caught.message : 'That change could not be saved.'),
        )
    }
    if (!disabled) return apply()
    requestConfirm({
      title: 'Disable user',
      message: `${user.email} will be signed out and will not be able to sign in.`,
      // Their work is not touched: boards they own stay, and so do shares they gave.
      consequence: 'Their boards and the shares they gave are kept.',
      confirmLabel: 'Disable user',
      undoable: false,
      onConfirm: apply,
    })
  }

  const setMcpEnabled = (enabled: boolean) => {
    const apply = () => {
      void repository
        .setTenantMcpEnabled(enabled)
        .then(setTenant)
        .catch((caught: unknown) =>
          setError(caught instanceof Error ? caught.message : 'That change could not be saved.'),
        )
    }
    if (enabled) return apply()
    requestConfirm({
      title: 'Turn off MCP access',
      message: 'Agents in your tenant will no longer be able to connect over MCP.',
      consequence: 'Integration tokens are kept and work again when MCP is turned back on.',
      confirmLabel: 'Turn off',
      undoable: false,
      onConfirm: apply,
    })
  }

  return (
    // The same shell as the boards dashboard and the tenants screen: a full-height page
    // with the brand bar on top. Without it this screen floated on the bare body, with
    // no chrome and no background of its own.
    <div className={styles.page}>
      <header className={styles.topBar}>
        <div className={styles.topRow}>
          <span className={styles.brand}>
            <img className={styles.logo} src="/logo-mark.png" alt="" width={34} height={18} />
            <BrandLockup />
          </span>
          <span className={styles.spacer} />
          <UserMenu />
        </div>
      </header>

      <div className={styles.scroll}>
        <div className={styles.inner}>
      <header className={styles.masthead}>
        <div className={styles.headings}>
          <h1 className={styles.title}>Users</h1>
          <p className={styles.subtitle}>
            {users ? `${users.length} user${users.length === 1 ? '' : 's'} in your tenant` : 'Loading…'}
          </p>
        </div>
        <button type="button" className={styles.primary} onClick={() => setCreating(true)}>
          New user
        </button>
        <Button onClick={onClose}>Back to boards</Button>
      </header>

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className={styles.hint} role="status">
          {notice}
        </p>
      ) : null}

      {tenant ? (
        <div className={styles.settingRow} data-testid="mcp-access">
          <span className={styles.strong}>MCP access</span>
          {tenant.mcpEnabled ? (
            <span className={styles.muted}>On</span>
          ) : (
            <span className={styles.disabledTag}>Off</span>
          )}
          <span className={styles.muted}>
            {tenant.mcpEnabled
              ? 'Agents can connect with integration tokens.'
              : 'Agents cannot connect. Integration tokens are kept.'}
          </span>
          {tenant.mcpEnabled ? (
            <Button variant="danger" onClick={() => setMcpEnabled(false)}>
              Turn off
            </Button>
          ) : (
            <Button onClick={() => setMcpEnabled(true)}>Turn on</Button>
          )}
        </div>
      ) : null}

      <table className={styles.table}>
        <thead>
          <tr>
            <th scope="col">Email</th>
            <th scope="col">Name</th>
            <th scope="col">Role</th>
            <th scope="col">Status</th>
            <th scope="col">
              <span className="visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {(users ?? []).map((user) => (
            <tr key={user.id} data-testid="user-row" data-user-email={user.email}>
              <td className={styles.strong}>{user.email}</td>
              <td>{user.displayName || <span className={styles.muted}>—</span>}</td>
              <td>{USER_ROLE_LABELS[user.role]}</td>
              <td>
                {user.disabled ? (
                  <span className={styles.disabledTag}>Disabled</span>
                ) : (
                  <span className={styles.muted}>Active</span>
                )}
              </td>
              <td className={styles.actions}>
                <Button onClick={() => setEditing(user)}>Edit</Button>
                {/* An invitation through Teamcentric Accounts not accepted yet. */}
                {accounts && user.invitationExpiresAt && !user.accountId ? (
                  <Button onClick={() => reinvite(user)}>Re-invite</Button>
                ) : null}
                {/* An admin cannot lock themselves out of their own tenant. */}
                {user.id === me?.id ? null : user.disabled ? (
                  <Button onClick={() => setDisabled(user, false)}>Enable</Button>
                ) : (
                  <Button variant="danger" onClick={() => setDisabled(user, true)}>
                    Disable
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

        </div>
      </div>

      <UserDialog
        user={editing}
        open={creating || editing !== null}
        accounts={accounts}
        onClose={() => {
          setCreating(false)
          setEditing(null)
        }}
        onSaved={(invitationSent) => {
          setCreating(false)
          setEditing(null)
          setNotice(invitationSent === undefined ? null : invitationOutcome(invitationSent))
          void refresh()
        }}
      />
    </div>
  )
}

/**
 * One form for both: creating a user invites them through Teamcentric Accounts, so there is
 * no password here. With Accounts on, someone with a teamcentric login has the name they
 * set there.
 */
function UserDialog({
  user,
  open,
  accounts,
  onClose,
  onSaved,
}: {
  user: User | null
  open: boolean
  accounts: boolean
  onClose: () => void
  /** `invitationSent` is given when the new user was invited through Teamcentric Accounts. */
  onSaved: (invitationSent?: boolean) => void
}) {
  const nameFromAccounts = accounts && Boolean(user?.accountId)
  const repository = sessionRepository()
  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<UserRole>('NORMAL_USER')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setEmail(user?.email ?? '')
    setDisplayName(user?.displayName ?? '')
    setRole(user?.role ?? 'NORMAL_USER')
    setError(null)
  }, [open, user])

  const submit = () => {
    if (busy) return
    setBusy(true)
    const done = (invitationSent?: boolean) => {
      setBusy(false)
      onSaved(invitationSent)
    }
    const failed = (caught: unknown) => {
      setBusy(false)
      setError(caught instanceof Error ? caught.message : 'That could not be saved.')
    }
    if (user) {
      repository
        .updateUser(user.id, {
          ...(nameFromAccounts ? {} : { displayName }),
          role,
        })
        .then(() => done(), failed)
    } else {
      repository
        .createUser({ email, displayName, role })
        .then((created) => done(created.invitationSent), failed)
    }
  }

  return (
    <Dialog
      open={open}
      title={user ? `Edit ${user.email}` : 'New user'}
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        <label className={styles.field}>
          <span className={styles.label}>Email</span>
          <input
            className={styles.input}
            type="email"
            required
            // The email is the person's identity and can't be changed in Blueprint.
            readOnly={Boolean(user)}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <span className={styles.hint}>
            {user
              ? "The email address can't be changed. If an invitation went to the wrong address, invite the right one."
              : 'Unique across the whole system.'}
          </span>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Display name</span>
          <input
            className={styles.input}
            value={displayName}
            // Teamcentric Accounts needs a name for the invitation.
            required={!user}
            disabled={nameFromAccounts}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          {nameFromAccounts ? <span className={styles.hint}>They change their name and photo at teamcentric Accounts.</span> : null}
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Role</span>
          <select
            className={styles.input}
            value={role}
            onChange={(event) => setRole(event.target.value as UserRole)}
          >
            <option value="NORMAL_USER">{USER_ROLE_LABELS.NORMAL_USER}</option>
            <option value="TENANT_ADMIN">{USER_ROLE_LABELS.TENANT_ADMIN}</option>
          </select>
        </label>
        {user ? null : (
          <p className={styles.hint}>Teamcentric Accounts emails them a link to set up their teamcentric login.</p>
        )}
        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  )
}
